import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * design-system.md's `Alert` row: 1px border, `radius-lg`, 10px/8px
 * horizontal/vertical padding, 14px text, a leading 16px icon with an 8px
 * gap. `destructive`/`warning` both use the `@40%` border / `@5%`
 * background / solid-text pattern on their own token — `warning` is a
 * project extension mirroring `destructive`'s own shape on the `warning`
 * token. (The doc's own prose says "10px radius" for this row while every
 * other row ties `radius-lg` to this project's actual `--radius: 0.5rem`
 * (8px) token — kept as the token, `rounded-lg`, not the stray 10px figure.)
 */
const alertVariants = cva(
  "relative grid w-full items-start gap-x-2 gap-y-0.5 rounded-lg border px-2.5 py-2 text-sm grid-cols-[0_1fr] has-[>svg]:grid-cols-[16px_1fr] [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive: "border-destructive/40 bg-destructive/5 text-destructive",
        warning: "border-warning/40 bg-warning/5 text-warning",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      data-slot="alert-title"
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      data-slot="alert-description"
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
